import { lazy, type ComponentType, type LazyExoticComponent } from "react";
import { Logo } from "./Logo";

/**
 * CODE-SPLIT PAGES THAT CANNOT LEAVE A BLANK SCREEN.
 *
 * Every page but the landing page — the game included — is a lazily imported chunk (main.tsx, AuthGate). Two
 * ways that went blank:
 *   • WHILE IT LOADED, the Suspense fallback was `null`, so a slow network (or a busy dev server) showed an
 *     empty page with nothing to say the game was coming. The fallback is now `BootSplash`.
 *   • WHEN IT FAILED, React threw the import error up a tree with no boundary and unmounted everything. The
 *     common real cause is a DEPLOY: a tab opened before it holds the old index, whose chunk names no longer
 *     exist on the server, so the first navigation to a lazy page (/play from the home page, after tabbing
 *     back) fails. `lazyPage` answers a failed chunk with ONE full reload, which fetches the current index and
 *     its chunks; a second failure inside the guard window is a real outage and is thrown on to AppBoundary,
 *     which shows a Reload card.
 *
 *   • WHEN IT STALLED — an import that neither resolves nor fails (a dropped connection mid-chunk; the dev
 *     server losing a module fetch under load) — the splash would have sat there for ever. After STALL_MS the
 *     stall is treated as a failure: the same single reload. Long enough not to restart a slow but moving
 *     download of the 2.9 MB game chunk on a poor connection.
 *
 * Retrying the import in place is pointless — browsers cache a failed dynamic import for the page's life.
 */
const RELOAD_KEY = "shelf-garden.chunk-reload-at";
const RELOAD_GUARD_MS = 30_000;
const STALL_MS = 25_000;

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- the same constraint React's own `lazy` uses
export function lazyPage<T extends ComponentType<any>>(load: () => Promise<{ default: T }>): LazyExoticComponent<T> {
  return lazy(async (): Promise<{ default: T }> => {
    try {
      let timer = 0;
      const stalled = new Promise<never>((_, rej) => { timer = window.setTimeout(() => rej(new Error(`page chunk still loading after ${STALL_MS / 1000}s`)), STALL_MS); });
      try { return await Promise.race([load(), stalled]); } finally { window.clearTimeout(timer); }
    } catch (e) {
      let last = 0;
      try { last = Number(sessionStorage.getItem(RELOAD_KEY) || 0); } catch { /* storage blocked */ }
      if (Date.now() - last > RELOAD_GUARD_MS) {
        try { sessionStorage.setItem(RELOAD_KEY, String(Date.now())); } catch { /* storage blocked */ }
        console.warn("[chunk] a page failed to load (likely a new deploy) — reloading once:", e);
        window.location.reload();
        return new Promise<never>(() => { /* the reload replaces this page */ });
      }
      throw e;
    }
  });
}

/** The splash while a lazy page (or the game) loads — in the ENTRY chunk, so it can show before any of them. */
export function BootSplash({ label = "Loading…" }: { label?: string }) {
  return (
    <div className="boot-splash" data-stage="chunk">
      <div className="boot-title"><Logo height={54} tone="light" /></div>
      <div className="boot-sub">{label}</div>
    </div>
  );
}
